// Cómo trabaja la empresa con las rentas: modalidad, tiempos, contrato y su texto.
import { el, clear } from '../../utils/dom.js';
import { Rentals, Settings } from '../../api/resources.js';
import { field, readForm } from '../../components/form.js';
import { ok, err } from '../../components/toast.js';

export default async function RentalsAjustesPage(outlet) {
  clear(outlet);
  let cfg;
  try { cfg = await Rentals.config(); }
  catch (ex) { outlet.innerHTML = `<div class="empty">Error: ${ex.message}</div>`; return; }

  outlet.appendChild(el('div', { class: 'page-head' },
    el('div', {}, el('h1', {}, 'Ajustes de rentas'), el('div', { class: 'muted' }, 'Cómo trabaja tu empresa con las rentas')),
    el('div', { class: 'flex gap-2 wrap' },
      el('a', { href: '#/rentals/mensajes', class: 'btn' }, 'Mensajes y WhatsApp'),
      el('a', { href: '#/rentals', class: 'btn ghost' }, '← Rentas')),
  ));

  const form = el('form', { onsubmit: async (e) => {
    e.preventDefault();
    const d = readForm(form);
    try {
      await Settings.bulk({
        'app.modo': { value: d.modo === 'tradicional' ? 'tradicional' : 'rental', description: 'tradicional = solo equipos; rental = con alquiler' },
        'rentals.buffer_minutos': { value: Math.max(0, Math.round(Number(d.buffer_h || 0) * 60 + Number(d.buffer_m || 0))), description: 'Preparación entre rentas (min)' },
        'rentals.tolerancia_atraso_min': { value: Math.max(0, Math.round(Number(d.tolerancia || 0))), description: 'Minutos de gracia antes de contar atraso' },
        'rentals.contrato_obligatorio': { value: !!d.contrato, description: 'No entregar sin contrato firmado' },
        'rentals.contrato_plantilla': { value: (d.plantilla || '').trim(), description: 'Texto del contrato de alquiler' },
        'avisos.horas_entrega': { value: Math.max(1, Number(d.av_ent || 24)), description: 'Horas antes de la entrega para avisar' },
        'avisos.horas_devolucion': { value: Math.max(1, Number(d.av_dev || 3)), description: 'Horas antes de la devolución para avisar' },
        'avisos.horas_solicitud': { value: Math.max(1, Number(d.av_sol || 2)), description: 'Horas antes de que venza una solicitud web' },
        'catalogo.activo': { value: !!d.cat_activo, description: 'Catálogo público de alquiler encendido' },
        'catalogo.titulo': { value: (d.cat_titulo || '').trim() || 'Alquiler de equipos', description: 'Título del catálogo' },
        'catalogo.whatsapp': { value: String(d.cat_whatsapp || '').replace(/\D/g, ''), description: 'WhatsApp para dudas' },
        'catalogo.terminos': { value: (d.cat_terminos || '').trim(), description: 'Condiciones que el cliente acepta al pedir' },
        'rentals.minutos_bloqueo': { value: Math.max(60, Math.round(Number(d.apartado_h || 24) * 60)), description: 'Apartado de una solicitud (min)' },
      });
      ok('Ajustes guardados');
      RentalsAjustesPage(outlet);
    } catch (ex) { err(ex.message); }
  } },
  el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Modalidad'),
    field({ name: 'modo', label: '¿Cómo usas el Inventario?', type: 'select', required: true, value: cfg.modo, options: [
      { value: 'tradicional', label: 'Solo controlar mis equipos (sin alquiler)' },
      { value: 'rental', label: 'Controlar y rentar mis equipos' },
    ] })),
  el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Tiempos'),
    el('p', { class: 'muted text-sm' }, 'Preparación: tiempo que un equipo queda ocupado después de la devolución, para revisarlo, cargarlo y limpiarlo.'),
    el('div', { class: 'form-grid' },
      field({ name: 'buffer_h', label: 'Preparación (horas)', type: 'number', min: 0, max: 168, value: Math.floor(cfg.buffer_minutos / 60) }),
      field({ name: 'buffer_m', label: 'y minutos', type: 'number', min: 0, max: 59, value: cfg.buffer_minutos % 60 }),
      field({ name: 'tolerancia', label: 'Minutos de gracia antes de contar atraso', type: 'number', min: 0, max: 1440, value: cfg.tolerancia_atraso_min }),
    )),
  el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Contrato'),
    field({ name: 'contrato', label: 'No entregar una renta sin el contrato firmado por el cliente', type: 'checkbox', value: cfg.contrato_obligatorio }),
    field({ name: 'plantilla', label: 'Texto del contrato', type: 'textarea', value: cfg.contrato_plantilla, rows: 18,
      hint: 'Puedes usar: {{empresa_nombre}} {{cliente_nombre}} {{cliente_documento}} {{renta_codigo}} {{renta_equipos}} {{renta_entrega}} {{renta_devolucion}} {{renta_dias}} {{renta_total}} {{renta_deposito}} {{fecha}}. Un contrato ya firmado no cambia.' })),
  el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Avisos al equipo'),
    el('p', { class: 'muted text-sm' }, 'Llegan a la campana una sola vez por renta.'),
    el('div', { class: 'form-grid' },
      field({ name: 'av_ent', label: 'Horas antes de una entrega', type: 'number', min: 1, max: 168, value: cfg.avisos?.horas_entrega ?? 24 }),
      field({ name: 'av_dev', label: 'Horas antes de una devolución', type: 'number', min: 1, max: 72, value: cfg.avisos?.horas_devolucion ?? 3 }),
      field({ name: 'av_sol', label: 'Horas antes de que venza una solicitud web', type: 'number', min: 1, max: 48, value: cfg.avisos?.horas_solicitud ?? 2 }),
    )),
  el('section', { class: 'card mb-4' },
    el('h3', { class: 'rp-h' }, 'Catálogo web'),
    el('p', { class: 'muted text-sm' }, 'Tus clientes ven los equipos que marques como "Mostrar en el catálogo web", arman su carrito con fechas y te mandan la solicitud. Los equipos quedan apartados mientras la apruebas.'),
    field({ name: 'cat_activo', label: 'Catálogo encendido', type: 'checkbox', value: cfg.catalogo?.activo }),
    cfg.catalogo?.activo ? el('p', { class: 'text-sm' }, 'Dirección: ', el('a', { href: `${location.origin}/catalogo`, target: '_blank', rel: 'noopener' }, `${location.origin}/catalogo`)) : null,
    el('div', { class: 'form-grid' },
      field({ name: 'cat_titulo', label: 'Título', value: cfg.catalogo?.titulo || 'Alquiler de equipos' }),
      field({ name: 'cat_whatsapp', label: 'WhatsApp para dudas (con código de país)', value: cfg.catalogo?.whatsapp || '', placeholder: '18095551234' }),
      field({ name: 'apartado_h', label: 'Horas que se apartan los equipos de una solicitud', type: 'number', min: 1, max: 720, value: Math.round(cfg.minutos_bloqueo / 60) }),
    ),
    field({ name: 'cat_terminos', label: 'Condiciones que el cliente acepta al pedir', type: 'textarea', value: cfg.catalogo?.terminos || '', rows: 5 })),
  el('button', { class: 'btn primary', type: 'submit' }, 'Guardar ajustes'));
  outlet.appendChild(form);
}
